import { Fragment, useEffect, useRef, useState } from 'react';

import circleIcon from '../../assets/icons/circle.svg';
import checkedIcon from '../../assets/icons/check.svg';
import chevronDown from '../../assets/icons/chevron-down.svg';
import { borderStyleConstants } from '../../constants/borderStyle';
import { useCloseDropdown } from '../../hooks/useCloseDropdown';
import { useSafeSelector } from '../../hooks/useSafeSelector';

export interface Option {
  label: string;
  value: string | number;
  icon?: string;
  [key: string]: any;
}

export interface DropdownProps {
  label: string;
  options: Option[];
  onChange: any;
  customStyle?: any;
  defaultOption?: string | number;
  className?: string;
  hideCircleIcon?: boolean;
  hideRemoveOptionBtn?: boolean;
  disableDropdown?: boolean;
}

export function Dropdown(props: DropdownProps) {
  const [option, setOption] = useState<Option | null>(null);
  const {
    label,
    options,
    onChange,
    customStyle,
    className,
    defaultOption,
    hideCircleIcon,
    hideRemoveOptionBtn,
    disableDropdown
  } = props;
  const [show, setShow] = useState(false);
  const { borderStyle } = useSafeSelector((state: any) => state.customization);
  const inputContainerRef = useCloseDropdown(setShow);
  const optionsRef = useRef<any[]>([]);
  const [typedLetters, setTypedLetters] = useState('');
  const typedTimeout = useRef<NodeJS.Timeout | null>(null);


  useEffect(() => {
    if (defaultOption) {
      const option = options.find((option) => option.value === defaultOption);
      if (option) {
        setOption(option);
        onChange(option);
      }
    }
  }, []);

  useEffect(() => {
    if (show) {
      const index = options.findIndex((o) => o.value === option?.value);
      if (index !== -1 && optionsRef.current[index]) {
        optionsRef.current[index].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [show, option]);

  const selectOption = (option: Option) => {
    setOption(option);
    onChange(option);
    setShow(false);
  };

  const removeSelectedOption = (e: any) => {
    e.stopPropagation();
    setOption(null);
    onChange();
    setShow(false);
  };

  const handleTypeToSelect = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!show) return;
      
    const char = e.key.length === 1 ? e.key : '';
    if (!char) return;

    const newTyped = typedLetters + char.toLowerCase();
    setTypedLetters(newTyped);

    if (typedTimeout.current) clearTimeout(typedTimeout.current);
    typedTimeout.current = setTimeout(() => {
      setTypedLetters('');
    }, 500);

    const matchIndex = options.findIndex((opt) =>
      opt.label.toLowerCase().startsWith(newTyped)
    );
    if (matchIndex !== -1) {
      setOption(options[matchIndex]);
      onChange(options[matchIndex]);
      const el = optionsRef.current[matchIndex];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }

  const handleShowDropDown = () => {
    if (disableDropdown) return;
    setShow((prev) => !prev);
  }

  return (
    <div ref={inputContainerRef} className="h-10 relative">
      <div
        className={`
            h-10 box-border flex items-center border  px-3 relative cursor-pointer border-gray-300 outline-none
           ${show ? 'border-[#7294FF] shadow-[0_0_2px_4px_#B9CAFF]' : ''}
           ${
             borderStyle === borderStyleConstants.RECTANGULAR
               ? 'rounded-none'
               : 'rounded-lg'
           } ${className}`}
        onClick={handleShowDropDown}
        style={customStyle}
        onKeyDown={handleTypeToSelect}
        tabIndex={0}
      >
        <div className="flex items-center justify-between text-sm font-normal w-full">
          <div className="flex items-center gap-2 h-8 cursor-pointer">
            {option && !hideCircleIcon && (
              <img
                className="w-auto h-5"
                src={circleIcon}
                alt={option.label}
              />
            )}
            {option?.icon && (
              <img
                className="w-auto h-5"
                src={option.icon}
                alt={option.label}
              />
            )}
            {option ? (
              <h6 className="text-sm block my-auto mx-0 text-gray-900">
                {option.label}
              </h6>
            ) : (
              <span className="text-sm font-normal text-gray-500">
                {label}
              </span>
            )}
          </div>
          {option && !hideRemoveOptionBtn ? (
            <div
              className="h-5 w-5 text-center text-gray-700 transition-all"
              onClick={removeSelectedOption}
            >
              <span>✖</span>
            </div>
          ) : (
            <div
              className={`
                  h-5 w-5 text-center text-gray-700 transition-all
                  ${show ? 'rotate-180' : 'rotate-0'}
                `}
            >
              <img
                className="w-5 h-5 object-contain"
                src={chevronDown}
                alt="down arrow"
              />
            </div>
          )}
        </div>
      </div>
      {show && (
        <ul
          tabIndex={-1}
          className="absolute py-2 top-10 max-h-56 overflow-y-auto w-full bg-white border rounded-lg shadow-lg z-20"
        >
          {options?.length ? (
            options.map((dropDownOption, index) => {
              return (
                <li
                  key={dropDownOption.value}
                  className={`flex items-center justify-between p-3 hover:bg-gray-100 cursor-pointer ${
                    option?.value === dropDownOption.value ? 'bg-gray-50' : ''
                  }`}
                  onClick={() => selectOption(dropDownOption)}
                  ref={(el) => (optionsRef.current[index] = el)}
                >
                  <div className='flex items-center gap-2'>
                    {!hideCircleIcon && (
                      <img
                        className="w-auto h-6"
                        src={circleIcon}
                        alt={dropDownOption.label}
                      />
                    )}
                    {dropDownOption?.icon && (
                      <img
                        className="w-auto h-6"
                        src={dropDownOption.icon}
                        alt={dropDownOption.label}
                      />
                    )}
                    <span className="text-sm font-medium block my-auto mx-0 text-gray-900">
                      {dropDownOption.label}
                    </span>
                  </div>
                  {option?.value === dropDownOption.value && (
                    <img src={checkedIcon} alt="checked" />
                  )}
                </li>
              );
            })
          ) : (
            <li className="py-3 px-4 flex h-8 cursor-pointer hover:bg-[#eeeff4] bg-[#eeeff4] justify-center">
              <span className="flex items-center">No options</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
